import React, { useState, useCallback, useMemo } from 'react';
import { Pagination } from 'antd';
import { useLocation } from 'react-router-dom';

import MainLayout from '../../components/Layout/MainLayout';
import BookCard from '../../components/shared/BookCard';
import EmptyState from '../../components/shared/EmptyState';
import { useApi } from '../../hooks/useApi';

import FilterPanel from './components/FilterPanel';
import ResultsHeader from './components/ResultsHeader';
import {
  PageWrapper,
  FilterColumn,
  ResultsColumn,
  ResultsGrid,
  PaginationWrapper,
} from './styles';

const PAGE_SIZE = 12;
const DEFAULT_PRICE_RANGE = [0, 25000];

export default function SearchResults() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const query = searchParams.get('q') || '';
  const categoria = searchParams.get('categoria') || '';

  const [publicationType, setPublicationType] = useState([]);
  const [condition, setCondition] = useState([]);
  const [coverType, setCoverType] = useState([]);
  const [language, setLanguage] = useState([]);
  const [priceRange, setPriceRange] = useState(DEFAULT_PRICE_RANGE);
  const [currentPage, setCurrentPage] = useState(1);

  const searchUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (categoria) params.set('category', categoria);
    condition.forEach((value) => params.append('condition', value));
    publicationType.forEach((value) => params.append('publicationType', value));
    coverType.forEach((value) => params.append('coverType', value));
    language.forEach((value) => params.append('language', value));
    if (priceRange[0] > 0) params.set('minPrice', priceRange[0]);
    if (priceRange[1] < DEFAULT_PRICE_RANGE[1]) params.set('maxPrice', priceRange[1]);
    params.set('page', currentPage - 1);
    params.set('size', PAGE_SIZE);
    return `/books?${params.toString()}`;
  }, [query, categoria, condition, publicationType, coverType, language, priceRange, currentPage]);

  const { data: pageData, loading } = useApi(searchUrl);

  const books = pageData?.content || [];
  const totalElements = pageData?.totalElements || 0;

  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const handlePublicationTypeChange = useCallback((values) => {
    setPublicationType(values);
    resetPage();
  }, [resetPage]);

  const handleConditionChange = useCallback((values) => {
    setCondition(values);
    resetPage();
  }, [resetPage]);

  const handleCoverTypeChange = useCallback((values) => {
    setCoverType(values);
    resetPage();
  }, [resetPage]);

  const handleLanguageChange = useCallback((values) => {
    setLanguage(values);
    resetPage();
  }, [resetPage]);

  const handlePriceRangeChange = useCallback((values) => {
    setPriceRange(values);
    resetPage();
  }, [resetPage]);

  const handleClearFilters = useCallback(() => {
    setPublicationType([]);
    setCondition([]);
    setCoverType([]);
    setLanguage([]);
    setPriceRange(DEFAULT_PRICE_RANGE);
    resetPage();
  }, [resetPage]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  return (
    <MainLayout>
      <ResultsHeader query={query} totalCount={totalElements} />
      <PageWrapper>
        <FilterColumn>
          <FilterPanel
            publicationType={publicationType}
            onPublicationTypeChange={handlePublicationTypeChange}
            condition={condition}
            onConditionChange={handleConditionChange}
            coverType={coverType}
            onCoverTypeChange={handleCoverTypeChange}
            language={language}
            onLanguageChange={handleLanguageChange}
            priceRange={priceRange}
            onPriceRangeChange={handlePriceRangeChange}
            onClearFilters={handleClearFilters}
          />
        </FilterColumn>
        <ResultsColumn>
          {!loading && books.length === 0 ? (
            <EmptyState
              description="No se encontraron resultados"
              actionText="Volver a buscar"
              actionLink="/buscar"
            />
          ) : (
            <>
              <ResultsGrid>
                {books.map((book) => (
                  <BookCard
                    key={book.id}
                    id={book.id}
                    title={book.title}
                    author={book.authorName}
                    price={book.price}
                    originalPrice={book.originalPrice}
                    condition={book.condition}
                    coverImage={book.coverImage}
                    showAddToCart
                  />
                ))}
              </ResultsGrid>
              {totalElements > PAGE_SIZE && (
                <PaginationWrapper>
                  <Pagination
                    current={currentPage}
                    pageSize={PAGE_SIZE}
                    total={totalElements}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                  />
                </PaginationWrapper>
              )}
            </>
          )}
        </ResultsColumn>
      </PageWrapper>
    </MainLayout>
  );
}
